/**
 * Card "Identidade Visual" do Painel WL.
 *
 * Replica visualmente a tela do painel admin, mas consome exclusivamente as
 * RPCs `wl_get_branding` / `wl_update_branding` (permissão `wl.settings`).
 * Botões de escrita ficam desabilitados quando o operador não tem `edit`.
 */
import { useRef, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Image as ImageIcon, Upload, Loader2, Trash2 } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { useWlBrandingSettings } from '@/hooks/wl/useWlBrandingSettings';
import { useWlPermissions } from '@/contexts/WlPermissionsContext';

const ACCEPTED_MIME = ['image/png', 'image/jpeg', 'image/svg+xml', 'image/webp'];

export default function WlBrandingSettingsCard() {
  const { branding, isLoading, updateBranding, isUpdating, uploadBrandingImage } =
    useWlBrandingSettings();
  const { canEdit } = useWlPermissions();
  const writable = canEdit('wl.settings');

  // Estados locais de preview e flags de upload
  const [appName, setAppName] = useState<string>('');
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [iconPreview, setIconPreview] = useState<string | null>(null);
  const [loginLogoPreview, setLoginLogoPreview] = useState<string | null>(null);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [isUploadingIcon, setIsUploadingIcon] = useState(false);
  const [isUploadingLoginLogo, setIsUploadingLoginLogo] = useState(false);

  const logoInputRef = useRef<HTMLInputElement>(null);
  const iconInputRef = useRef<HTMLInputElement>(null);
  const loginLogoInputRef = useRef<HTMLInputElement>(null);

  // -----------------------------------------------------------------------
  // Helpers
  // -----------------------------------------------------------------------
  const validateFile = (file: File, maxMb: number): boolean => {
    if (!ACCEPTED_MIME.includes(file.type)) {
      toast({
        title: 'Formato inválido',
        description: 'Use imagens PNG, JPG, SVG ou WebP.',
        variant: 'destructive',
      });
      return false;
    }
    if (file.size > maxMb * 1024 * 1024) {
      toast({
        title: 'Arquivo muito grande',
        description: `O tamanho máximo é ${maxMb}MB.`,
        variant: 'destructive',
      });
      return false;
    }
    return true;
  };

  const handleUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
    type: 'logo' | 'icon' | 'login_logo',
    maxMb: number,
    setPreview: (v: string | null) => void,
    setUploading: (v: boolean) => void,
    inputRef: React.RefObject<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!validateFile(file, maxMb)) return;

    try {
      setUploading(true);
      const reader = new FileReader();
      reader.onload = (ev) => setPreview(ev.target?.result as string);
      reader.readAsDataURL(file);

      const url = await uploadBrandingImage(file, type);
      const field =
        type === 'logo' ? 'logo_url' : type === 'icon' ? 'icon_url' : 'login_logo_url';
      await updateBranding({ [field]: url } as any);
      setPreview(null);
    } catch (err) {
      // Toast já é exibido pelo hook
      setPreview(null);
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleSaveAppName = async () => {
    // Sanitização básica anti-XSS no client (backend valida também)
    const clean = appName.replace(/<[^>]*>/g, '').trim();
    if (!clean || clean.length > 60) {
      toast({
        title: 'Nome inválido',
        description: 'O nome do sistema deve ter entre 1 e 60 caracteres.',
        variant: 'destructive',
      });
      return;
    }
    await updateBranding({ app_name: clean });
  };

  // URLs efetivas (preview local sobrepõe a do storage)
  const currentLogoUrl = logoPreview || branding?.logo_url;
  const currentIconUrl = iconPreview || branding?.icon_url;
  const currentLoginLogoUrl = loginLogoPreview || branding?.login_logo_url;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ImageIcon className="h-5 w-5" />
          Identidade Visual
        </CardTitle>
        <CardDescription>
          Configure a logo, ícone e nome do sistema exibidos no login e menus
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Nome do Sistema */}
        <div className="space-y-2">
          <Label htmlFor="wl-app-name">Nome do Sistema</Label>
          <div className="flex gap-2">
            <Input
              id="wl-app-name"
              value={appName || branding?.app_name || ''}
              onChange={(e) => setAppName(e.target.value)}
              placeholder="UaizyChat"
              className="max-w-xs"
              maxLength={60}
              disabled={!writable}
            />
            <Button
              onClick={handleSaveAppName}
              disabled={!writable || isUpdating || appName === branding?.app_name || !appName}
              size="sm"
            >
              {isUpdating ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Salvar'}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">Exibido no login e no menu lateral</p>
        </div>

        {/* Logo Completa */}
        <BrandingImageRow
          label="Logo Completa"
          hint="Recomendado: 200x50px, PNG ou SVG com fundo transparente"
          previewClass="w-48 h-16"
          currentUrl={currentLogoUrl}
          fallbackText="Sem logo"
          inputRef={logoInputRef}
          isUploading={isUploadingLogo}
          writable={writable}
          hasSaved={!!branding?.logo_url}
          isUpdating={isUpdating}
          onUpload={(e) =>
            handleUpload(e, 'logo', 2, setLogoPreview, setIsUploadingLogo, logoInputRef)
          }
          onRemove={() => updateBranding({ logo_url: null })}
        />

        {/* Logo Tela de Login */}
        <BrandingImageRow
          label="Logo da Tela de Login"
          hint="Recomendado: 300x100px, PNG ou SVG com fundo transparente. Se não configurada, será usada a Logo Completa."
          previewClass="w-48 h-16"
          currentUrl={currentLoginLogoUrl}
          fallbackText="Sem logo de login"
          inputRef={loginLogoInputRef}
          isUploading={isUploadingLoginLogo}
          writable={writable}
          hasSaved={!!branding?.login_logo_url}
          isUpdating={isUpdating}
          onUpload={(e) =>
            handleUpload(
              e,
              'login_logo',
              2,
              setLoginLogoPreview,
              setIsUploadingLoginLogo,
              loginLogoInputRef
            )
          }
          onRemove={() => updateBranding({ login_logo_url: null })}
        />

        {/* Ícone */}
        <BrandingImageRow
          label="Ícone"
          hint="Recomendado: 64x64px, quadrado, PNG ou SVG"
          previewClass="w-16 h-16"
          currentUrl={currentIconUrl}
          fallbackText="Sem ícone"
          inputRef={iconInputRef}
          isUploading={isUploadingIcon}
          writable={writable}
          hasSaved={!!branding?.icon_url}
          isUpdating={isUpdating}
          onUpload={(e) =>
            handleUpload(e, 'icon', 1, setIconPreview, setIsUploadingIcon, iconInputRef)
          }
          onRemove={() => updateBranding({ icon_url: null })}
        />

        {/* Preview combinado */}
        <div className="pt-4 border-t border-border">
          <Label className="mb-3 block">Preview</Label>
          <div className="flex items-center gap-6 flex-wrap">
            <div className="bg-sidebar border border-border rounded-lg p-3 flex items-center gap-2">
              {currentIconUrl ? (
                <img src={currentIconUrl} alt="" className="w-8 h-8 object-contain" />
              ) : (
                <div className="w-8 h-8 bg-primary rounded flex items-center justify-center text-primary-foreground font-bold text-sm">
                  {(appName || branding?.app_name || 'U')[0].toUpperCase()}
                </div>
              )}
              <span className="font-semibold text-sidebar-foreground">
                {appName || branding?.app_name || 'UaizyChat'}
              </span>
            </div>
            <div className="bg-sidebar border border-border rounded-lg p-3 flex items-center justify-center w-14 h-14">
              {currentIconUrl ? (
                <img src={currentIconUrl} alt="" className="w-8 h-8 object-contain" />
              ) : (
                <div className="w-8 h-8 bg-primary rounded flex items-center justify-center text-primary-foreground font-bold text-sm">
                  {(appName || branding?.app_name || 'U')[0].toUpperCase()}
                </div>
              )}
            </div>
          </div>
        </div>

        {!writable && (
          <p className="text-xs text-muted-foreground italic">
            Você não tem permissão para editar estas configurações.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

// =========================================================================
// Subcomponente reutilizado para Logo / Login Logo / Ícone
// =========================================================================
interface BrandingImageRowProps {
  label: string;
  hint: string;
  previewClass: string;
  currentUrl: string | null | undefined;
  fallbackText: string;
  inputRef: React.RefObject<HTMLInputElement>;
  isUploading: boolean;
  writable: boolean;
  hasSaved: boolean;
  isUpdating: boolean;
  onUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemove: () => void;
}

function BrandingImageRow({
  label,
  hint,
  previewClass,
  currentUrl,
  fallbackText,
  inputRef,
  isUploading,
  writable,
  hasSaved,
  isUpdating,
  onUpload,
  onRemove,
}: BrandingImageRowProps) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <p className="text-xs text-muted-foreground mb-2">{hint}</p>
      <div className="flex items-start gap-4">
        <div
          className={`${previewClass} border border-dashed border-border rounded-lg flex items-center justify-center bg-muted/30 overflow-hidden`}
        >
          {currentUrl ? (
            <img src={currentUrl} alt={label} className="max-w-full max-h-full object-contain" />
          ) : (
            <span className="text-xs text-muted-foreground px-2 text-center">{fallbackText}</span>
          )}
        </div>
        <div className="flex flex-col gap-2">
          <input
            ref={inputRef}
            type="file"
            accept="image/png,image/jpeg,image/svg+xml,image/webp"
            className="hidden"
            onChange={onUpload}
          />
          <Button
            variant="outline"
            size="sm"
            onClick={() => inputRef.current?.click()}
            disabled={!writable || isUploading}
          >
            {isUploading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Upload className="h-4 w-4 mr-2" />
            )}
            {currentUrl ? 'Substituir' : 'Upload'}
          </Button>
          {hasSaved && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onRemove}
              disabled={!writable || isUpdating}
              className="text-destructive hover:text-destructive"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Remover
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
